<template>
  <div class="status-extended d-flex align-content-center flex-row justify-content-center">
    <div class="mt-10">
      <div class="row justify-content-between align-items-center">
        <div class="text-center status-page-nav-bar">
          <router-link :to="{ name: 'app-base' }" tag="button" class="btn btn-secondary btn-with-icon mono">
            <i class="mdp-ChevronLeft mr-1"></i>
            {{ translate("BackToHome") }}
          </router-link>
        </div>
        <locale-switcher :show-icon="true"></locale-switcher>
      </div>
      <div class="row system-status mt-10">
        <div class="col-lg-12">
          <b-card no-body>
            <b-tabs>
              <b-tab :title="translate('about')">
                <div class="card-body">
                  <div class="status-title">
                    <h3>{{ translate("statuspage.Status") }}</h3>
                    <div>
                      <button class="btn btn-secondary btn-with-icon mono" @click="runHealthChecks"><i class="mdp-Refresh"></i></button>
                    </div>
                  </div>
                  <b-card-text>
                    <div class="d-flex status-node-container">
                      <status-node
                        :link="viewModel.health.connectivity.url"
                        :is-link-interactive="true"
                        :title="translate(viewModel.health.connectivity.title)"
                        :description="viewModel.health.connectivity.feedback"
                        :status="viewModel.health.connectivity.status"></status-node>
                      <status-node
                        :title="translate(viewModel.health.clientCompatibility.title)"
                        :description="viewModel.health.clientCompatibility.feedback"
                        :status="viewModel.health.clientCompatibility.status"></status-node>
                      <status-node
                        :title="translate(viewModel.health.media.title)"
                        :description="viewModel.health.media.feedback"
                        :status="viewModel.health.media.status"></status-node>
                      <status-node
                        :link="viewModel.health.databaseStatus.url"
                        :title="translate(viewModel.health.databaseStatus.title)"
                        :description="viewModel.health.databaseStatus.feedback"
                        :status="viewModel.health.databaseStatus.status"></status-node>
                      <status-node
                        :title="translate(viewModel.health.databaseCompatibility.title)"
                        :description="viewModel.health.databaseCompatibility.feedback"
                        :status="viewModel.health.databaseCompatibility.status"></status-node>
                      <status-node
                        :title="translate(viewModel.health.databseScripts.title)"
                        :description="viewModel.health.databseScripts.feedback"
                        :status="viewModel.health.databseScripts.status"></status-node>
                    </div>
                    <div class="d-flex flex-row bottom-pane">
                      <div class="license-panel">
                        <h3>{{ translate("statuspage.licenseInformation") }}</h3>

                        <div class="license-list">
                          <div class="license-node" v-for="(value, key) of viewModel.licenses" :key="key">
                            <span>{{ translate(`licenses.${key}`) }}</span>
                            <span class="badge badge-pill" v-safe-class="getLicenseType(value)">{{ value }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="support-panel">
                        <h3>{{ translate("statuspage.needAssistance") }}</h3>
                        <div>{{ translate("statuspage.weAnswerQuestions") }}</div>
                        <p>
                          {{ translate("statuspage.firstPointOfContact") }}
                        </p>

                        <img src="@assets/support.png" class="support-img" />
                        <h4>
                          {{ translate("statuspage.supportTeamWillHelp") }}
                          <div class="sub-footer">{{ translate("statuspage.fastHelp") }}</div>
                        </h4>

                        <div class="contact-modes">
                          <div class="contact-type">
                            <div class="contact-body">
                              <div class="heading">{{ translate("statuspage.callUs") }}</div>
                              <div>{{ translate("statuspage.callTiming") }}</div>
                            </div>
                            <div class="contact-footer"><i class="icon mdp-Phone"></i><span>+49 (0) 2842 - 92 737 77</span></div>
                          </div>
                          <div class="contact-type">
                            <div class="contact-body">
                              <div class="heading">{{ translate("statuspage.emailUs") }}</div>
                            </div>
                            <div class="contact-footer justify-content-between">
                              <span class="d-flex align-items-center"><i class="icon mdp-Mail"></i><span>support@itb-pim.de</span></span>
                              <a class="btn btn-primary" href="mailto:support@itb-pim.de">{{ translate("statuspage.emailButtonText") }}</a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-card-text>
                </div>
              </b-tab>
              <b-tab :title="$t('logs')">
                <api-log-viewer></api-log-viewer>
              </b-tab>
            </b-tabs>
            <template #footer>
              <div class="d-flex justify-content-between">
                <div class="footer-branding">MeDaPro Everywhere {{ "20" + meta.version }} ({{ removeRevivisonFromVersion(meta.version) }})</div>
                <div class="footer-logo">
                  <a href="#" @click.prevent="viewModel.showThirdPartyLicenseDialog = true">{{ translate("licenses.thirdPartyLicenses") }}</a>
                  <img src="@assets/logo2.svg" />
                </div>
              </div>
            </template>
          </b-card>
        </div>
      </div>
    </div>
    <b-modal
      size="lg"
      dialog-class="third-party-licenses"
      footer-class="border-0"
      scrollable
      ok-only
      :ok-title="translate('close')"
      v-model="viewModel.showThirdPartyLicenseDialog"
      header-close-content=""
      :title="translate('licenses.thirdPartyLicenses')">
      <div>
        <div class="license-item" v-for="(item, index) in viewModel.thirdPartyLicenses" :key="index">
          <pre>{{ item }}</pre>
        </div>
      </div>
    </b-modal>
  </div>
</template>
<script lang="ts" setup>
import healthChecksApi from "@services/healthChecks";
import statusApi from "@services/status.js";
import { onMounted, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import StatusNode from "./StatusNode.vue";
const translate = useI18n().t;

import ApiLogViewer from "@common/components/ApiLogViewer.vue";
import localeSwitcher from "@common/components/LocaleSwitcher.vue";
import { RootState } from "@medapro-types/common/rootstate";
import { HealthCheckStatus, IHealthCheckModel, IVersionResponse, NodeData, NodeStatus } from "@medapro-types/services/healthChecks";
import _ from "lodash";
import { useState } from "vuex-composition-helpers";

const { meta, userSettings, licenses } = useState<RootState>(["meta", "userSettings", "licenses"]);

const viewModel = reactive({
  nodes: [] as NodeData[],
  isConnected: false,
  colorCodedLicenseStatus: false, //Change this to true if license values need to be color coded.
  licenses: {},
  thirdPartyLicenses: [],
  health: {
    connectivity: { title: "statuspage.title.connectivity", status: NodeStatus.Unknown, feedback: "", url: meta.value.config.apiUrl },
    clientCompatibility: {
      title: "statuspage.title.clientCompatibility",
      status: NodeStatus.Unknown,
      feedback: "",
      data: undefined as IVersionResponse | undefined
    },
    databaseStatus: {
      title: "statuspage.title.databaseConnectivity",
      status: NodeStatus.Unknown,
      feedback: "",
      url: "",
      data: undefined as IHealthCheckModel | undefined
    },
    databaseCompatibility: {
      title: "statuspage.title.databaseCompatibility",
      status: NodeStatus.Unknown,
      feedback: "",
      databaseVersion: "",
      clientVersion: "",
      data: undefined as IHealthCheckModel | undefined
    },
    databseScripts: { title: "statuspage.title.sqlComponents", status: NodeStatus.Unknown, feedback: "", data: undefined as IHealthCheckModel | undefined },
    media: { title: "statuspage.title.media", status: NodeStatus.Unknown, feedback: "", data: undefined as IHealthCheckModel | undefined }
  },
  showThirdPartyLicenseDialog: false
});

/* eslint-disable @typescript-eslint/no-unused-vars */
async function getVersion() {
  try {
    viewModel.health.clientCompatibility.status = NodeStatus.Running;
    viewModel.health.connectivity.status = NodeStatus.Running;
    viewModel.health.connectivity.feedback = translate("statuspage.connectionSuccessful", { url: meta.value.config.apiUrl });
    viewModel.health.clientCompatibility.feedback = translate("statuspage.checkClientVersion");

    const result = await healthChecksApi.getVersion();
    viewModel.isConnected = true;
    viewModel.health.connectivity.status = NodeStatus.Success;

    const apiVersion = (viewModel.health.clientCompatibility.data = result);
    if (apiVersion && apiVersion.version) {
      const api = apiVersion.version.split(".").slice(0, 3);
      const client = (meta.value.version || "").split(".").slice(0, 3);

      //viewModel.health.clientCompatibility.feedback = `Client Version: ${meta.value.version} is  Api version: ${apiVersion.version}`;
      const sources = _.sortBy([api, client], "length"); // sort for iterataing on item with highest number of version segments.
      if (sources[1].every((e, i) => sources[0][i] == e)) {
        viewModel.health.clientCompatibility.status = NodeStatus.Success;
        viewModel.health.clientCompatibility.feedback = translate("statuspage.feedback.clientIsEqual", {
          client: removeRevivisonFromVersion(meta.value.version ?? ""),
          api: removeRevivisonFromVersion(apiVersion.version ?? "")
        });
      } else {
        const isBehindApi = parseInt(api.join("") || "0") > parseInt(client.join("") || "0");
        viewModel.health.clientCompatibility.feedback = translate(
          isBehindApi ? "statuspage.feedback.clientIsBehindApi" : "statuspage.feedback.clientIsAheadApi",
          { client: removeRevivisonFromVersion(meta.value.version ?? ""), api: removeRevivisonFromVersion(apiVersion.version ?? "") }
        );
        if (api[0] === client[0] && api[1] === client[1]) {
          viewModel.health.clientCompatibility.status = NodeStatus.Warning;
        } else {
          viewModel.health.clientCompatibility.status = NodeStatus.Error;
        }
      }
    } else {
      viewModel.health.clientCompatibility.status = NodeStatus.Error;
      viewModel.health.clientCompatibility.feedback = translate("statuspage.versionCheckUnknownResponse");
    }
  } catch (error: any) {
    viewModel.health.connectivity.status = NodeStatus.Error;
    viewModel.health.clientCompatibility.status = NodeStatus.Error;
    viewModel.health.clientCompatibility.feedback = error && error.feedback ? error.feedback : error.message || "network error";
  }
}

/* eslint-disable @typescript-eslint/no-unused-vars */
async function GetDatabaseStatus() {
  try {
    if (!viewModel.isConnected) {
      viewModel.health.databaseStatus.status = NodeStatus.Unknown;
    } else {
      viewModel.health.databaseStatus.status = NodeStatus.Running;
      const result = await healthChecksApi.getDatabaseStatus();
      viewModel.health.databaseStatus.status = NodeStatus.Success;
      if (result.errorType != "ServerError") {
        viewModel.health.databaseStatus.data = result;
        viewModel.health.databaseStatus.feedback = translate("statuspage.feedback.DatabaseConnectionSuccessful");
        viewModel.health.databaseStatus.url = result.params.dataSource + "/" + result.params.database;
      } else viewModel.health.databaseStatus.feedback = translate(result.feedback);
    }
  } catch (error: any) {
    viewModel.health.databaseStatus.status = NodeStatus.Error;
    viewModel.health.databaseStatus.feedback = error && error.feedback ? error.feedback : error.message || "network error";
  }
}

async function getDatabaseCompatibility() {
  try {
    if (!viewModel.isConnected) {
      viewModel.health.databaseCompatibility.status = NodeStatus.Unknown;
    } else {
      viewModel.health.databaseCompatibility.status = NodeStatus.Running;
      const result = await healthChecksApi.getDatabaseCompatibility();
      viewModel.health.databaseCompatibility.status = getStatusString(result.success);
      if (result.errorType != "ServerError") {
        viewModel.health.databaseCompatibility.data = result;
        viewModel.health.databaseCompatibility.databaseVersion = result.params["databaseVersion"];
        viewModel.health.databaseCompatibility.clientVersion = result.params["clientVersion"];
        const versionStatus = compareVersions(viewModel.health.databaseCompatibility.clientVersion, viewModel.health.databaseCompatibility.databaseVersion);
        const dbVersionMessage =
          versionStatus == -1
            ? "statuspage.feedback.DatabaseVersionAheadClient"
            : versionStatus == 1
              ? "statuspage.feedback.DatabaseVersionBehindClient"
              : "statuspage.feedback.DatabaseCompatibleWithClient";

        viewModel.health.databaseCompatibility.feedback = translate(dbVersionMessage, {
          clientVersion: removeRevivisonFromVersion(result.params["clientVersion"]),
          databaseVersion: result.params["databaseVersion"]
        });
      } else viewModel.health.databaseCompatibility.feedback = translate(result.feedback);
    }
  } catch (error: any) {
    viewModel.health.databaseCompatibility.status = NodeStatus.Error;
    viewModel.health.databaseCompatibility.feedback = error && error.feedback ? translate(error.feedback) : error.message || "network error";
  }
}

function removeRevivisonFromVersion(version: string): string {
  const parts = version.split(".");
  if (parts.length <= 3) return version;
  return parts.slice(0, 3).join(".");
}

function compareVersions(v1: string, v2: string) {
  const c1 = v1.split(".").slice(0, 3);
  const c2 = v2.split(".").slice(0, 3);

  const max = Math.max(c1.length, c2.length);
  for (let index = 0; index < max; index++) {
    const part1 = parseInt(c1[index] || "0");
    const part2 = parseInt(c2[index] || "0");

    if (part1 < part2) {
      return -1;
    }
    if (part1 > part2) {
      return 1;
    }
  }

  return 0;
}

async function getDatabaseScriptStatus() {
  try {
    if (viewModel.isConnected == false) {
      viewModel.health.databseScripts.status = NodeStatus.Unknown;
    } else {
      viewModel.health.databseScripts.status = NodeStatus.Running;
      const result = await healthChecksApi.getDatabaseScriptStatus();
      viewModel.health.databseScripts.data = result;
      viewModel.health.databseScripts.status = getStatusString(result.success);
      if (result.errorType != "ServerError") viewModel.health.databseScripts.feedback = translate("statuspage.feedback." + result.params.feedback);
      else viewModel.health.databseScripts.feedback = result.feedback;
    }
  } catch (error: any) {
    viewModel.health.databseScripts.status = NodeStatus.Error;
    viewModel.health.databseScripts.feedback = error && error.feedback ? translate(error.feedback) : error.message || "network error";
  }
}

async function getMediaAssetPaths() {
  try {
    if (viewModel.isConnected == false) {
      viewModel.health.media.status = NodeStatus.Unknown;
    } else {
      viewModel.health.media.status = NodeStatus.Running;
      const result = await healthChecksApi.getMediaPathStatus();
      viewModel.health.media.data = result;
      viewModel.health.media.status = getStatusString(result.success);
      viewModel.health.media.feedback =
        result.success == HealthCheckStatus.OK
          ? translate("statuspage.feedback." + result.params.feedback)
          : result.errorType == "ServerError"
            ? result.feedback
            : translate("statuspage.feedback." + result.errorType);
    }
  } catch (error: any) {
    viewModel.health.media.status = NodeStatus.Error;
    viewModel.health.media.feedback = error && error.feedback ? error.feedback : error.message || "network error";
  }
}

async function runHealthChecks() {
  await getVersion();
  if (!viewModel.isConnected) {
    return;
  }

  await Promise.all([GetDatabaseStatus(), getDatabaseCompatibility(), getDatabaseScriptStatus(), getMediaAssetPaths(), getLicenseInformation()]);
}

async function getLicenseInformation() {
  try {
    viewModel.licenses = licenses.value;
    viewModel.thirdPartyLicenses = await statusApi.getThirdPartyLicence();
  } catch (error) {
    viewModel.licenses = {};
  }
}

function getLicenseType(v: string): string {
  if (!viewModel.colorCodedLicenseStatus) {
    return "badge-secondary";
  }

  if (v == "NotLicensed") return "badge-warning";
  else if (!v || v == "0") return "badge-secondary";

  return "badge-success";
}

function getStatusString(status: HealthCheckStatus): NodeStatus {
  switch (status) {
    case HealthCheckStatus.ERROR:
      return NodeStatus.Error;
    case HealthCheckStatus.OK:
      return NodeStatus.Success;
    case HealthCheckStatus.WARN:
      return NodeStatus.Warning;
  }

  return NodeStatus.Unknown;
}

watch(
  () => userSettings.value.CurrentLocale,
  () => runHealthChecks()
);

onMounted(runHealthChecks);
</script>
